import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import classnames from 'classnames';

export enum StepEnum {
  OrderConfirmation = 'confirmation',
  OrderPayment = 'payment',
}

interface Props {
  activeStep: StepEnum;
  className?: string;
  onStepClick?: (step: StepEnum) => void;
}

export default function CheckoutSteps({ activeStep, className, onStepClick }: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:CheckoutSteps' });
  const [hoveredStep, setHoveredStep] = useState<StepEnum | null>(null);

  const steps = useMemo(
    () => [
      {
        id: StepEnum.OrderConfirmation,
        name: t(`steps.${StepEnum.OrderConfirmation}`),
        isActive: activeStep === StepEnum.OrderConfirmation,
        isComplete: activeStep === StepEnum.OrderPayment,
      },
      {
        id: StepEnum.OrderPayment,
        name: t(`steps.${StepEnum.OrderPayment}`),
        isActive: activeStep === StepEnum.OrderPayment,
        isComplete: false,
      },
    ],
    [activeStep, i18n.language],
  );

  return (
    <div className={classnames('sticky z-40 flex items-center justify-center gap-2 py-lg', className)}>
      {steps.map(({ id, name, isActive, isComplete }, index) => {
        const isClickable = isComplete && !!onStepClick;
        const isHovered = hoveredStep === id;

        return (
          <div
            key={id}
            className={classnames('flex items-center gap-2', { 'cursor-pointer': isClickable })}
            onClick={() => isClickable && onStepClick(id)}
            onMouseEnter={() => isClickable && setHoveredStep(id)}
            onMouseLeave={() => isClickable && setHoveredStep(null)}
          >
            {index > 0 && <div className="w-8 h-px bg-muted" />}
            <div className="flex items-center gap-1.5">
              <div
                className={classnames(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold bg-muted text-muted-foreground transition-colors',
                  {
                    '!bg-success !text-white': isComplete && !isHovered,
                    '!bg-transparent !text-success border border-success': isComplete && isHovered,
                    '!bg-black !text-white': isActive,
                  },
                )}
              >
                {isComplete ? isHovered ? index + 1 : <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>
              <span
                className={classnames('text-xs font-medium', {
                  'text-black': isActive,
                  'text-muted-foreground': !isActive,
                })}
              >
                {name}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
